import * as React from 'react'
import { Link } from 'react-router-dom'
import { BarChart, BarList, DonutChart, type CustomTooltipProps } from '@tremor/react'
import {
  AlertTriangle, ArrowRight, Database, Eye, Info, Trash2, Zap,
} from 'lucide-react'
import { useDashboard } from '@/lib/queries'
import { useAuth } from '@/lib/auth'
import { PageHeader } from '@/components/layout/AppShell'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/misc'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import { useFeatureLabels } from '@/components/ui/status'
import { cn, formatDateTime, formatNumber } from '@/lib/utils'

const trendSeries = [
  { key: '状态变化', color: 'blue', dotClassName: 'bg-blue-500' },
  { key: '可注册确认', color: 'emerald', dotClassName: 'bg-emerald-500' },
  { key: 'Zone新增', color: 'cyan', dotClassName: 'bg-cyan-500' },
  { key: 'Zone移除', color: 'amber', dotClassName: 'bg-amber-500' },
] as const

type TrendSeriesKey = (typeof trendSeries)[number]['key']

function EventTrendTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null

  const rows = payload.filter((item) => Number(item.value ?? 0) > 0)
  if (rows.length === 0) return null

  return (
    <div className="relative z-50 min-w-48 overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-xl ring-1 ring-black/5">
      <div className="border-b bg-muted/50 px-3.5 py-2.5 text-sm font-semibold">
        {String(label ?? '')}
      </div>
      <div className="space-y-2 px-3.5 py-3">
        {rows.map((item) => {
          const name = String(item.name ?? item.dataKey ?? '')
          const series = trendSeries.find((entry) => entry.key === name)
          return (
            <div key={name} className="flex items-center gap-2.5 text-sm">
              <span
                className={cn('h-2.5 w-2.5 shrink-0 rounded-sm', series?.dotClassName ?? 'bg-slate-400')}
                aria-hidden
              />
              <span className="min-w-0 flex-1 whitespace-nowrap text-muted-foreground">{name}</span>
              <span className="pl-5 font-semibold tabular">{formatNumber(Number(item.value ?? 0))}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function DashboardPage() {
  const { user } = useAuth()
  const [days, setDays] = React.useState(7)
  const { data, isLoading, error, refetch } = useDashboard(days)
  const featureLabels = useFeatureLabels()

  if (isLoading) {
    return (
      <>
        <PageHeader title="概览" />
        <LoadingState rows={6} label="正在加载概览" />
      </>
    )
  }
  if (error) {
    return (
      <>
        <PageHeader title="概览" />
        <ErrorState error={error} onRetry={() => void refetch()} />
      </>
    )
  }
  if (!data) return null

  const kpis = [
    { key: 'shared_domains', label: '共享域名总数', icon: Database, to: '/domains' },
    { key: 'discovered_today', label: '今日新发现', icon: Zap, to: '/domains?sort=created_at' },
    { key: 'pending_confirmation', label: '待确认', icon: Info, to: '/domains?status=unknown' },
    { key: 'pending_delete', label: 'Pending Delete', icon: Trash2, to: '/domains?status=pendingDelete' },
    { key: 'available', label: '可注册', icon: Zap, to: '/domains?status=available' },
    { key: 'my_watchers', label: '监控中', icon: Eye, to: '/monitoring' },
    { key: 'key_events_today', label: '今日关键事件', icon: AlertTriangle, to: '/events' },
  ]

  const statusData = Object.entries(data.facets.by_status)
    .map(([name, value]) => ({ name: statusLabel(name), value }))
    .filter((d) => d.value > 0)

  const tldData = Object.entries(data.facets.by_tld)
    .map(([name, value]) => ({ name: `.${name}`, value, href: `/domains?tld=${name}` }))
    .sort((a, b) => b.value - a.value)

  const featureData = Object.entries(data.facets.by_feature)
    .map(([name, value]) => ({
      name: featureLabels.get(name)?.label ?? name,
      value,
      href: `/domains?feature=${name}`,
    }))
    .sort((a, b) => b.value - a.value)

  const trend = data.trend.map((point) => ({
    date: new Date(point.day).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' }),
    状态变化: point.counts.status_changed ?? 0,
    可注册确认: point.counts.availability_confirmed ?? 0,
    Zone新增: point.counts.zone_added ?? 0,
    Zone移除: point.counts.zone_removed ?? 0,
  }))

  const trendTotals = trendSeries.reduce<Record<TrendSeriesKey, number>>((totals, series) => {
    totals[series.key] = trend.reduce((sum, point) => sum + point[series.key], 0)
    return totals
  }, { '状态变化': 0, '可注册确认': 0, Zone新增: 0, Zone移除: 0 })
  const activeTrendSeries = trendSeries.filter((series) => trendTotals[series.key] > 0)
  const hasTrendEvents = activeTrendSeries.length > 0

  return (
    <>
      <PageHeader
        title="概览"
        description={`数据生成于 ${formatDateTime(data.generated_at, user?.timezone)}`}
        actions={
          <Tabs value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <TabsList>
              <TabsTrigger value="1">24 小时</TabsTrigger>
              <TabsTrigger value="7">7 日</TabsTrigger>
              <TabsTrigger value="30">30 日</TabsTrigger>
            </TabsList>
          </Tabs>
        }
      />

      {data.anomalies.length > 0 && (
        <div className="mb-5 space-y-2">
          {data.anomalies.map((a) => (
            <div
              key={a.kind}
              role={a.severity === 'critical' ? 'alert' : 'status'}
              className={cn(
                'flex items-start gap-2 rounded-lg border px-3 py-2 text-sm',
                a.severity === 'critical' && 'border-destructive/40 bg-destructive/5 text-destructive',
                a.severity === 'warning' && 'border-amber-500/40 bg-amber-500/5 text-amber-800 dark:text-amber-300',
                a.severity === 'info' && 'border-border bg-muted/50 text-muted-foreground',
              )}
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span className="flex-1">{a.message}</span>
              {a.count != null && <Badge variant="outline">{formatNumber(a.count)}</Badge>}
              {a.admin_only && <Badge variant="muted">管理员</Badge>}
            </div>
          ))}
        </div>
      )}

      <section aria-label="关键指标" className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        {kpis.map((kpi) => {
          const Icon = kpi.icon
          return (
            <Link
              key={kpi.key}
              to={kpi.to}
              className="rounded-lg border bg-card p-4 transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon className="h-3.5 w-3.5" aria-hidden />
                <span className="truncate text-xs">{kpi.label}</span>
              </div>
              <p className="mt-2 text-2xl font-semibold tabular">
                {formatNumber(data.kpi[kpi.key] ?? 0)}
              </p>
            </Link>
          )
        })}
      </section>

      <section className="mb-5 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>事件趋势</CardTitle>
            <CardDescription>
              生命周期事件按天统计。Zone 新增/移除是观察事实，不代表注册状态变化。
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hasTrendEvents ? (
              <EmptyState
                title="暂无事件"
                description="导入 Zone 数据或运行规则后，这里会显示生命周期事件趋势。"
              />
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="事件数量汇总">
                  {trendSeries.map((series) => (
                    <div key={series.key} className="rounded-lg border bg-muted/20 px-3 py-2.5">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className={cn('h-2 w-2 rounded-sm', series.dotClassName)} aria-hidden />
                        <span>{series.key}</span>
                      </div>
                      <p className="mt-1 text-lg font-semibold tabular">{formatNumber(trendTotals[series.key])}</p>
                    </div>
                  ))}
                </div>
                <BarChart
                  data={trend}
                  index="date"
                  categories={activeTrendSeries.map((series) => series.key)}
                  colors={activeTrendSeries.map((series) => series.color)}
                  valueFormatter={formatNumber}
                  className="h-60"
                  stack
                  showAnimation={false}
                  showLegend={activeTrendSeries.length > 1}
                  showGridLines
                  allowDecimals={false}
                  yAxisWidth={52}
                  barCategoryGap="28%"
                  customTooltip={EventTrendTooltip}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>我的规则命中</CardTitle>
            <CardDescription>待确认结果尚未经外部确认，不代表可注册</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-xs text-muted-foreground">已确认命中</p>
                <p className="text-xl font-semibold tabular">{formatNumber(data.my_matches.matched)}</p>
              </div>
              <Badge variant="success">matched</Badge>
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-xs text-muted-foreground">待确认</p>
                <p className="text-xl font-semibold tabular">
                  {formatNumber(data.my_matches.pending_verification)}
                </p>
              </div>
              <Badge variant="warning">pending</Badge>
            </div>
            <Button variant="outline" size="sm" className="w-full" asChild>
              <Link to="/rules">
                查看规则 <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>状态分布</CardTitle>
          </CardHeader>
          <CardContent>
            {statusData.length === 0 ? (
              <EmptyState title="暂无数据" />
            ) : (
              <DonutChart
                data={statusData}
                category="value"
                index="name"
                colors={['emerald', 'amber', 'orange', 'red', 'teal', 'slate', 'gray']}
                className="h-52"
                showAnimation={false}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>后缀分布</CardTitle>
            <CardDescription>点击可跳转到已预筛选的域名库</CardDescription>
          </CardHeader>
          <CardContent>
            {tldData.length === 0 ? <EmptyState title="暂无数据" /> : <BarList data={tldData} className="mt-2" />}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>特征分布</CardTitle>
            <CardDescription>计算特征，可重算</CardDescription>
          </CardHeader>
          <CardContent>
            {featureData.length === 0 ? (
              <EmptyState title="暂无数据" />
            ) : (
              <BarList data={featureData} className="mt-2" color="cyan" />
            )}
          </CardContent>
        </Card>
      </section>

      <section className="mt-5">
        <Card>
          <CardHeader>
            <CardTitle>本期用量</CardTitle>
            <CardDescription>
              成本型计量单位为 external_query 与 active monitored domain；缓存命中不计费
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {data.usage.items.map((item) => {
                const pct = item.unlimited ? 0 : Math.min(100, (item.used / Math.max(item.limit, 1)) * 100)
                const near = !item.unlimited && pct >= 80
                return (
                  <div key={item.entitlement} className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">{item.label}</p>
                    <p className="mt-1 text-sm font-medium tabular">
                      {formatNumber(item.used)}
                      <span className="text-muted-foreground">
                        {' / '}{item.unlimited ? '不限' : formatNumber(item.limit)}
                      </span>
                    </p>
                    {!item.unlimited && (
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn('h-full rounded-full', near ? 'bg-amber-500' : 'bg-primary')}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                    {near && (
                      <p className="mt-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                        接近上限（超额策略：{item.over_policy}）
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      </section>

      <p className="mt-5 flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          平台提供的是发现、监控与第三方数据汇总。可注册性、释放时间与注册结果均不构成保证，
          最终以注册商结果为准。
        </span>
      </p>
    </>
  )
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    registered: '已注册',
    expired: '已过期',
    redemptionPeriod: '赎回期',
    pendingDelete: '删除等待期',
    available: '可注册',
    reserved: '保留',
    unknown: '未知',
  }
  return map[status] ?? status
}
